/**
 * ConfirmCloseDialog Component
 * T101: Confirmation dialog for discarding form changes
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  GlassModal,
  GlassModalContent,
  GlassModalHeader,
  GlassModalTitle,
  GlassModalDescription,
  GlassModalFooter,
} from '@/components/common/GlassModal';

export interface ConfirmCloseDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when user confirms (discard changes) */
  onConfirm: () => void;
  /** Called when user cancels (keep editing) */
  onCancel: () => void;
}

/**
 * Confirmation dialog shown when user tries to close form with unsaved changes.
 * Uses inline width style to bypass Tailwind v4 class resolution issues
 * when rendered as a nested Radix dialog.
 */
export function ConfirmCloseDialog({
  isOpen,
  onConfirm,
  onCancel,
}: ConfirmCloseDialogProps) {
  const { t } = useTranslation();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onCancel();
    }
  };

  return (
    <GlassModal open={isOpen} onOpenChange={handleOpenChange}>
      <GlassModalContent
        showCloseButton={false}
        style={{ width: 'calc(100vw - 2rem)', maxWidth: '28rem' }}
      >
        <GlassModalHeader>
          <GlassModalTitle>{t('confirmCloseDialog.title')}</GlassModalTitle>
          <GlassModalDescription>
            {t('confirmCloseDialog.description')}
          </GlassModalDescription>
        </GlassModalHeader>
        <GlassModalFooter className="flex-row justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={onConfirm}
            className="border border-gray-300 bg-white text-[var(--color-charcoal)] hover:bg-gray-100"
          >
            {t('confirmCloseDialog.discard')}
          </Button>
          <Button
            onClick={onCancel}
            className="btn-primary border-0"
          >
            {t('confirmCloseDialog.keepEditing')}
          </Button>
        </GlassModalFooter>
      </GlassModalContent>
    </GlassModal>
  );
}

export default ConfirmCloseDialog;
